import { useState, useRef, useEffect } from 'react';
import { NetworkElementType } from '../../types/network';
import { useNetworkUIStore } from '../../store/networkUIStore';
import { useCouplingStore } from '../../store/couplingStore';

const elementTypeLabels: Record<NetworkElementType, string> = {
  trafostation: 'Trafostationen',
  abgangsleitung: 'Kabel',
  kabelverteilschrank: 'Kabelverteiler',
  hausanschluss: 'Hausanschlüsse',
  muffe: 'Muffen'
};

export const ElementFilter = () => {
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const [isOpen, setIsOpen] = useState(false);
  const visibleElementTypes = useNetworkUIStore(state => state.visibleElementTypes);
  const toggleElementTypeVisibility = useNetworkUIStore(state => state.toggleElementTypeVisibility);
  const setAllElementTypesVisible = useNetworkUIStore(state => state.setAllElementTypesVisible);
  const showTrennstellen = useCouplingStore(state => state.showTrennstellen);
  const setShowTrennstellen = useCouplingStore(state => state.setShowTrennstellen);

  const handleToggleAll = (visible: boolean) => {
    setAllElementTypesVisible(visible);
  };

  const allVisible = visibleElementTypes.size === Object.keys(elementTypeLabels).length;

  return (
    <div className="relative">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full px-3 py-2 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-100 hover:text-gray-900 dark:text-primary-200 dark:hover:bg-primary-700 dark:hover:text-white transition-colors duration-150 focus:outline-hidden focus-visible:ring-2 focus-visible:ring-accent-primary flex items-center space-x-3"
      >
        <svg className="w-5 h-5 shrink-0 text-accent-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
        </svg>
        <span className="flex-1 text-left">Elementtypen</span>
        {!allVisible && (
          <span className="shrink-0 text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-accent-primary/15 text-accent-primary">
            {visibleElementTypes.size}/{Object.keys(elementTypeLabels).length}
          </span>
        )}
      </button>

      {isOpen && (
        <div ref={dropdownRef} className="absolute left-0 mt-2 w-56 bg-white dark:bg-primary-700 border border-gray-300 dark:border-primary-500 rounded-md shadow-lg z-50">
          <div className="p-2 space-y-2">
            <div className="flex items-center px-2 py-1 space-x-2">
              <label className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  checked={allVisible}
                  onChange={(e) => handleToggleAll(e.target.checked)}
                  className="rounded border-gray-300 dark:border-primary-500 text-accent-primary focus:ring-accent-primary bg-white dark:bg-primary-600"
                  aria-label="Alle Elemente anzeigen oder ausblenden"
                />
                <span className="text-sm font-medium text-gray-700 dark:text-primary-100">
                  Alle {allVisible ? 'ausblenden' : 'anzeigen'}
                </span>
              </label>
            </div>

            <div className="border-t border-gray-200 dark:border-primary-600" />

            {(Object.entries(elementTypeLabels) as [NetworkElementType, string][]).map(([type, label]) => (
              <div key={type} className="flex items-center px-2 py-1 space-x-2">
              <label className="flex items-center space-x-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={visibleElementTypes.has(type)}
                  onChange={() => toggleElementTypeVisibility(type)}
                  className="rounded border-gray-300 dark:border-primary-500 text-accent-primary focus:ring-accent-primary bg-white dark:bg-primary-600 cursor-pointer"
                  aria-label={`${label} anzeigen oder ausblenden`}
                />
                <span className="text-sm text-gray-700 dark:text-primary-200">{label}</span>
              </label>
              </div>
            ))}

            <div className="border-t border-gray-200 dark:border-primary-600" />
            <div className="flex items-center px-2 py-1 space-x-2">
              <label className="flex items-center space-x-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={showTrennstellen}
                  onChange={(e) => setShowTrennstellen(e.target.checked)}
                  className="rounded border-gray-300 dark:border-primary-500 text-accent-primary focus:ring-accent-primary bg-white dark:bg-primary-600"
                />
                <span className="text-sm text-gray-700 dark:text-primary-100">Trennstellen</span>
              </label>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
